import { TFunction } from 'i18next';

import { ProductTimelineEventDTO } from 'services/repositories/masterdata/masterdata';

import { ProductRow } from '../../helpers/productRows';

/**
 * Who the Log tooltip names as the actor for one audit entry — pulled out of `ProductLogCell` so
 * the selection logic is unit-testable directly, without needing the tooltip to actually open
 * (which the rendering pipeline it lives inside can't exercise reliably in this test setup: the
 * trigger icon is a `jest.fn()` heroicons mock, so the ref/handlers `TooltipTrigger` clones onto
 * it never reach a real DOM node, and `TooltipContent` renders `null` until the tooltip is open).
 */
export const resolveProductLogActor = (log: NonNullable<ProductRow['latest_log']>, t: TFunction): string => {
  // `created_by.isAdmin` exists specifically so a Procuros admin's real name is never shown to a
  // partner user — masking that takes priority over showing the name at all.
  if (log.created_by) {
    return log.created_by.isAdmin ? t('settings:productMasterdata.table.log.procurosSupportTeam') : log.created_by.name;
  }

  // `latest_log.meta` is `RelationshipTimelineEvent | ProductTimelineEvent | null` in the
  // generated schema because `TimelineEvent` is shared across domains; `latest_log` on a product
  // is always a `PRODUCT_CREATED`/`PRODUCT_UPDATED` event, so its `meta` is always the product
  // variant (which is the only one carrying `origin`).
  const origin = (log.meta as ProductTimelineEventDTO | null | undefined)?.origin;
  return t(
    origin === 'ADMIN_PORTAL'
      ? 'settings:productMasterdata.table.log.adminTool'
      : origin === 'AUTOMATICALLY' || origin === 'NOVA'
        ? 'settings:productMasterdata.table.log.automated'
        : 'settings:productMasterdata.table.log.unknownUser',
  );
};

/**
 * i18n keys
 * t('settings:productMasterdata.table.log.procurosSupportTeam')
 * t('settings:productMasterdata.table.log.adminTool')
 * t('settings:productMasterdata.table.log.automated')
 * t('settings:productMasterdata.table.log.unknownUser')
 */
